import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users, KeyRound } from 'lucide-react';

const ROLES = ['admin', 'standard_user'];
const ROLE_COLORS = {
  admin: 'bg-blue-100 text-blue-800',
  standard_user: 'bg-slate-100 text-slate-700',
  super_admin: 'bg-purple-100 text-purple-800'
};

export default function AdminUsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ username: '', password: '', full_name: '', role: 'standard_user', location_id: '' });
  const [resetDialog, setResetDialog] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [u, l] = await Promise.all([axios.get(`${API}/users`), axios.get(`${API}/locations`)]);
      setUsers(u.data);
      setLocations(l.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const openCreate = () => {
    setForm({ username: '', password: '', full_name: '', role: 'standard_user', location_id: '' });
    setSelected(null);
    setDialog('create');
  };
  const openEdit = (u) => {
    setForm({ username: u.username, password: '', full_name: u.full_name || '', role: u.role, location_id: u.location_id || '' });
    setSelected(u);
    setDialog('edit');
  };
  const closeDialog = () => { setDialog(null); setSelected(null); };

  const handleSubmit = async () => {
    if (!form.username) { toast.error('Email address required'); return; }
    if (dialog === 'create' && !form.password) { toast.error('Password required'); return; }
    try {
      const payload = { ...form, location_id: form.location_id || null };
      if (dialog === 'edit' && !payload.password) delete payload.password;
      if (dialog === 'create') {
        await axios.post(`${API}/users`, payload);
        toast.success('User created');
      } else {
        await axios.put(`${API}/users/${selected.id}`, payload);
        toast.success('User updated');
      }
      closeDialog();
      fetchAll();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  const toggleActive = async (u) => {
    try {
      await axios.put(`${API}/users/${u.id}`, { active: !u.active });
      toast.success(`User ${u.active ? 'deactivated' : 'activated'}`);
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await axios.delete(`${API}/users/${id}`);
      toast.success('User deleted');
      fetchAll();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setResetting(true);
    try {
      await axios.post(`${API}/users/${resetDialog.id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset for ${resetDialog.username}`);
      setResetDialog(null);
      setNewPassword('');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to reset password'); }
    finally { setResetting(false); }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Users</h1>
          <p className="text-slate-500 text-sm mt-1">Manage team members and their access</p>
        </div>
        <Button onClick={openCreate} className="gap-2 font-semibold"
          className="btn-primary" style={{ background: '#1D4ED8', color: 'white' }}
          data-testid="admin-users-create-button">
          <Plus size={14} /> Add User
        </Button>
      </div>
      <Card className="bg-white shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-100">
                <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wide">User</TableHead>
                <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Role</TableHead>
                <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Location</TableHead>
                <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Status</TableHead>
                <TableHead className="text-right text-xs font-semibold text-slate-600 uppercase tracking-wide">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(3)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(5)].map((_, j) => <TableCell key={j}><div className="h-4 bg-slate-100 rounded animate-pulse" /></TableCell>)}
                  </TableRow>
                ))
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-12 text-center">
                    <Users className="mx-auto mb-2 text-slate-300" size={32} />
                    <p className="text-slate-500">No users yet</p>
                  </TableCell>
                </TableRow>
              ) : (
                users.map(u => {
                  const locName = locations.find(l => l.id === u.location_id)?.name;
                  return (
                    <TableRow key={u.id} className="data-row border-slate-50">
                      <TableCell>
                        <div>
                          <p className="text-sm font-medium text-slate-800">{u.full_name || u.username}</p>
                          <p className="text-xs text-slate-500">{u.username}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`text-xs border-0 ${ROLE_COLORS[u.role] || 'bg-slate-100 text-slate-700'}`}>
                          {u.role?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">{locName || '— All'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch checked={u.active !== false} onCheckedChange={() => toggleActive(u)}
                            disabled={u.id === currentUser?.id} />
                          <span className="text-xs text-slate-500">{u.active !== false ? 'Active' : 'Inactive'}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:text-amber-500" title="Reset Password"
                            onClick={() => { setResetDialog(u); setNewPassword(''); }}>
                            <KeyRound size={13} />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(u)}>
                            <Pencil size={13} />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:text-red-500"
                            onClick={() => handleDelete(u.id)}
                            disabled={u.id === currentUser?.id}>
                            <Trash2 size={13} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Dialog open={!!dialog} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading">{dialog === 'create' ? 'Add User' : 'Edit User'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Username *</Label>
                <Input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })}
                  placeholder="user@clinic.com" className="bg-white" />
              </div>
              <div className="space-y-1">
                <Label>Full Name</Label>
                <Input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })}
                  placeholder="John Smith" className="bg-white" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>{dialog === 'create' ? 'Password *' : 'New Password (leave blank to keep)'}</Label>
              <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                placeholder={dialog === 'edit' ? 'Leave blank to keep current' : 'Secure password'} className="bg-white" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Role *</Label>
                <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                  <SelectTrigger className="bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {ROLES.map(r => <SelectItem key={r} value={r}>{r.replace('_', ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Location (optional)</Label>
                <Select value={form.location_id || 'none'} onValueChange={v => setForm({ ...form, location_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="All locations" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="none">All locations</SelectItem>
                    {locations.filter(l => l.active).map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="bg-white" onClick={closeDialog}>Cancel</Button>
            <Button onClick={handleSubmit}
              className="btn-primary" style={{ background: '#1D4ED8', color: 'white' }}>
              {dialog === 'create' ? 'Create User' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetDialog} onOpenChange={o => !o && setResetDialog(null)}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <KeyRound size={16} className="text-amber-500" /> Reset Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-600">
              Setting new password for <strong>{resetDialog?.username}</strong>
            </p>
            <div className="space-y-1">
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Min. 6 characters"
                className="bg-white"
                onKeyDown={e => e.key === 'Enter' && handleResetPassword()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="bg-white" onClick={() => setResetDialog(null)}>Cancel</Button>
            <Button onClick={handleResetPassword} disabled={resetting}
              className="bg-amber-500 hover:bg-amber-400 text-white">
              {resetting ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
